import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { AuditLogDiff, ResourceType } from "@common/types/auditLog";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { HistoryDetailsBaseRow } from "@components/history/details/cells/historyDetailsBaseRow";
import { LogTimeCell } from "@components/history/details/cells/LogTimeCell";
import { MemberCell } from "../cells/MemberCell";
import { ClientsCell } from "../cells/ClientsCell";

export enum OrganizationActivityDetailsColumn {
    TIME = "logDate",
    MEMBER = "member",
    ROLE = "roleLabel",
    CLIENTS = "clients",
    FIELD = "fieldLabel",
    PREVIOUS = "previous",
    MODIFIED = "modified",
}

export interface OrganizationActivityCreateDeleteDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    diff: AuditLogDiff[];
    clientWorkspaceIds: string[];
    roleLabel: string;
}

export interface OrganizationActivityUpdateDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    diff: AuditLogDiff[];
    clientWorkspaceIds: string[];
    fieldLabel: string;
    previous: string;
    modified: string;
}

export const defaultOrganizationActivityCreateDeleteDetailsView: View = {
    type: ViewType.HISTORY_UPDATED_TRANSACTION,
    columns: [
        { id: OrganizationActivityDetailsColumn.TIME },
        { id: OrganizationActivityDetailsColumn.MEMBER },
        { id: OrganizationActivityDetailsColumn.ROLE },
        { id: OrganizationActivityDetailsColumn.CLIENTS },
    ],
    filters: {},
    options: {
        selection: false,
        density: ViewDensity.MD,
    },
};

export const createDefaultOrganizationActivityUpdateDetailsView = (
    hasMultipleFields: boolean,
): View => ({
    type: ViewType.HISTORY_UPDATED_TRANSACTION,
    columns: [
        { id: OrganizationActivityDetailsColumn.TIME },
        { id: OrganizationActivityDetailsColumn.MEMBER },
        ...(hasMultipleFields
            ? [{ id: OrganizationActivityDetailsColumn.FIELD }]
            : []),
        { id: OrganizationActivityDetailsColumn.PREVIOUS },
        { id: OrganizationActivityDetailsColumn.MODIFIED },
    ],
    filters: {},
    options: {
        selection: false,
        density: ViewDensity.MD,
    },
});

export const createOrganizationActivityCreateDeleteViewConfig = (): ViewConfig<
    OrganizationActivityCreateDeleteDetailsRow,
    OrganizationActivityDetailsColumn,
    never
> => ({
    columns: {
        [OrganizationActivityDetailsColumn.TIME]: {
            name: "Time",
            header: "",
            accessorKey: "logDate",
            cell: LogTimeCell,
            ...DATE_COLUMN_SIZE,
        },
        [OrganizationActivityDetailsColumn.MEMBER]: {
            name: "Member",
            header: "Member",
            accessorKey: "resourceId",
            cell: MemberCell,
            minSize: 220,
            maxSize: 340,
        },
        [OrganizationActivityDetailsColumn.ROLE]: {
            name: "Role",
            header: "Role",
            accessorKey: "roleLabel",
            minSize: 120,
            maxSize: 180,
        },
        [OrganizationActivityDetailsColumn.CLIENTS]: {
            name: "Clients",
            header: "Clients",
            accessorKey: "clientWorkspaceIds",
            cell: ClientsCell,
            minSize: 120,
            maxSize: 200,
        },
        [OrganizationActivityDetailsColumn.FIELD]: {
            name: "Field",
            header: "Field",
            accessorKey: "roleLabel",
            minSize: 140,
            maxSize: 220,
        },
        [OrganizationActivityDetailsColumn.PREVIOUS]: {
            name: "Previous",
            header: "Previous",
            accessorKey: "roleLabel",
            minSize: 180,
            maxSize: 280,
        },
        [OrganizationActivityDetailsColumn.MODIFIED]: {
            name: "Modified",
            header: "Modified",
            accessorKey: "roleLabel",
            minSize: 180,
            maxSize: 280,
        },
    },
});

export const createOrganizationActivityUpdateViewConfig = (
    data: OrganizationActivityUpdateDetailsRow[],
): ViewConfig<
    OrganizationActivityUpdateDetailsRow,
    OrganizationActivityDetailsColumn,
    never
> => {
    const fieldLabels = [...new Set(data.map(({ fieldLabel }) => fieldLabel))];
    const singleFieldLabel =
        fieldLabels.length === 1 ? fieldLabels[0].toLowerCase() : null;
    const previousLabel = singleFieldLabel
        ? `Previous ${singleFieldLabel}`
        : "Previous";
    const modifiedLabel = singleFieldLabel
        ? `Modified ${singleFieldLabel}`
        : "Modified";

    return {
        columns: {
            [OrganizationActivityDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [OrganizationActivityDetailsColumn.MEMBER]: {
                name: "Member",
                header: "Member",
                accessorKey: "resourceId",
                cell: MemberCell,
                minSize: 200,
                maxSize: 320,
            },
            [OrganizationActivityDetailsColumn.ROLE]: {
                name: "Role",
                header: "Role",
                accessorKey: "fieldLabel",
                minSize: 120,
                maxSize: 180,
            },
            [OrganizationActivityDetailsColumn.CLIENTS]: {
                name: "Clients",
                header: "Clients",
                accessorKey: "clientWorkspaceIds",
                cell: ClientsCell,
                minSize: 120,
                maxSize: 200,
            },
            [OrganizationActivityDetailsColumn.FIELD]: {
                name: "Field",
                header: "Field",
                accessorKey: "fieldLabel",
                minSize: 140,
                maxSize: 220,
            },
            [OrganizationActivityDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                minSize: 180,
                maxSize: 300,
            },
            [OrganizationActivityDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                minSize: 180,
                maxSize: 300,
            },
        },
    };
};
